import { ApplicationRef, provideZoneChangeDetection } from '@angular/core'
import { bootstrapApplication } from '@angular/platform-browser'

import { MaintenancePageComponent } from './maintenance-page.component'
import { loadMaintenanceConfig, MAINTENANCE_CONFIG, MaintenanceConfig } from './maintenance.config'

export interface BootstrapWithMaintenanceDeps {
  loadConfig?: () => Promise<MaintenanceConfig>
  bootstrapMaintenance?: (config: MaintenanceConfig) => Promise<ApplicationRef>
}

export function bootstrapMaintenancePage(config: MaintenanceConfig): Promise<ApplicationRef> {
  return bootstrapApplication(MaintenancePageComponent, {
    providers: [
      provideZoneChangeDetection({ eventCoalescing: true }),
      { provide: MAINTENANCE_CONFIG, useValue: config }
    ]
  }).then((appRef) => {
    // Same reveal as the app root: drop the inline boot loader.
    document.body.classList.add('loaded')
    document.getElementById('app-loader')?.remove()
    return appRef
  })
}

/**
 * Reads the runtime maintenance flag before anything else runs. When it is on,
 * the static maintenance page is bootstrapped instead of the application, so
 * none of the application's initializers (auth, environment, websocket, i18n)
 * execute and no request reaches the API. When it is off, `bootstrapApp` runs
 * unchanged.
 */
export async function bootstrapWithMaintenance(
  bootstrapApp: () => Promise<ApplicationRef>,
  deps: BootstrapWithMaintenanceDeps = {}
): Promise<ApplicationRef> {
  const config = await (deps.loadConfig ?? loadMaintenanceConfig)()
  if (config.enabled) {
    return (deps.bootstrapMaintenance ?? bootstrapMaintenancePage)(config)
  }
  return bootstrapApp()
}
